/** Passkey sign-in and setup, using the browser's built-in WebAuthn through SimpleWebAuthn. */
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import { api } from "./api.ts";

export const passkeysSupported = () => browserSupportsWebAuthn();

/** With a username, the browser offers only that account's passkeys. */
export async function signInWithPasskey(username?: string): Promise<void> {
  const optionsJSON = await api<Parameters<typeof startAuthentication>[0]["optionsJSON"]>(
    "/api/auth/login/options",
    { body: username?.trim() ? { username: username.trim() } : {} },
  );
  const response = await startAuthentication({ optionsJSON });
  await api("/api/auth/login/verify", { body: { response } });
}

/** First-time setup (with the setup code) or adding another passkey while signed in. */
export async function registerPasskey(
  opts: {
    name?: string;
    setupToken?: string;
    inviteToken?: string;
    label?: string;
    signup?: boolean;
    username?: string;
    requests?: { groupId: string; personId: string }[];
    joins?: { groupId: string; personId: string | null }[];
    /** A passkey link: the passkey is for the account it was made for. */
    passkeyToken?: string;
  } = {},
): Promise<void> {
  const optionsJSON = await api<Parameters<typeof startRegistration>[0]["optionsJSON"]>(
    "/api/auth/register/options",
    {
      body: {
        name: opts.name,
        setupToken: opts.setupToken,
        inviteToken: opts.inviteToken,
        signup: opts.signup,
        username: opts.username,
        requests: opts.requests,
        joins: opts.joins,
        passkeyToken: opts.passkeyToken,
      },
    },
  );
  const response = await startRegistration({ optionsJSON });
  await api("/api/auth/register/verify", {
    body: { response, label: opts.label ?? deviceLabel() },
  });
}

export async function devSignIn(member?: string): Promise<void> {
  await api("/api/auth/dev-login", { body: member ? { member } : {} });
}

/** A friendly name for the passkey list, like "iPhone" or "Mac". */
function deviceLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android";
  if (/Mac/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows";
  return "This device";
}

/** Turns WebAuthn's technical errors into plain words. */
export function passkeyError(e: unknown): string {
  const name = (e as { name?: string })?.name;
  if (name === "NotAllowedError" || name === "AbortError") return "Passkey request was cancelled.";
  if (name === "InvalidStateError") return "This device already has a passkey here.";
  return (e as Error)?.message ?? "Something went wrong with the passkey.";
}
